/**
 * =============================================================================
 * USE QUESTION MEDIA HOOK (ADR-022 §10(A)/(B) — PR4b, BL-IMG-ADMIN-AUTHORING-001)
 * =============================================================================
 *
 * Owns the LIVE media list for ONE question inside the question-detail modal
 * plus the three management mutations mounted there: upload (question add),
 * remove (soft-delete), and hide/show. The modal receives a STALE
 * `question.images` snapshot from the queue row — this hook is the fresh
 * source of truth: it fetches the QUESTION-anchored gallery on open
 * (`anchor: 'question'` + an explicit limit, fold C1) and re-fetches after
 * every applied management mutation, so the section always renders server
 * state. Uploads land the SERVER-returned DTO (prepend, newest-first order
 * preserved — fold E1) rather than refetching per file; the row is still the
 * server's, never a synthesized local one.
 *
 * Defensive scaffolding mirrors the house hooks (useMedia / useRfi):
 *   - list fetches run under an AbortController (a newer fetch supersedes an
 *     in-flight one; a stale result never lands),
 *   - mutations are serialized by a SYNCHRONOUS in-flight ref (two same-tick
 *     invocations issue exactly one API call) mirrored into `isMutating`,
 *   - unmount guard on every setState.
 *
 * Toast policy: remove / hide / show report through useToast (single-action
 * management buttons). Upload does NOT toast — AdminImageUploader renders the
 * per-file outcome inline against the file row.
 * =============================================================================
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdminMediaListItem, MediaModerationAction } from '@find-my-part/types';
import { adminApi } from '../api/client';
import { useToast } from './useToast';
import {
  resolveMediaErrorMessage,
  useMediaAuthoring,
  type MediaUploadOutcome,
} from './useMediaAuthoring';

/** Visibility toggles this hook applies (subset of MediaModerationAction). */
export type MediaVisibilityAction = Extract<MediaModerationAction, 'HIDE' | 'SHOW'>;

/**
 * List limit for the question gallery (fold C1). The default limit (20) plus
 * reply-anchored rows silently truncated a busy question's own gallery; 50
 * comfortably exceeds the per-question cap + any legacy rows while staying
 * inside the backend clamp [1, 100].
 */
export const QUESTION_MEDIA_LIST_LIMIT = 50;

/**
 * Options for the useQuestionMedia hook.
 */
export interface UseQuestionMediaOptions {
  /** Question ID whose media this hook manages ('' when no question is open). */
  questionId: string;
  /** Auth token. */
  token: string;
  /** Fetch gate — false while the modal is closed (no fetch, state reset). */
  enabled: boolean;
}

/**
 * Return value of the useQuestionMedia hook.
 */
export interface UseQuestionMediaResult {
  /**
   * The fetched live media rows, or null before the first successful load —
   * the modal renders the stale `question.images` snapshot until then.
   */
  media: AdminMediaListItem[] | null;
  /** Whether a list fetch is in flight. */
  isLoading: boolean;
  /** Whether a remove/hide/show mutation is in flight. */
  isMutating: boolean;
  /** Re-fetch the media list (post-mutation sync / manual refresh). */
  refresh: () => Promise<void>;
  /** Upload one image to the question, then refresh. Uploader outcome shape. */
  uploadImage: (file: File) => Promise<MediaUploadOutcome>;
  /** Soft-delete one attachment, then refresh. Toasts the outcome. */
  removeImage: (attachmentId: string) => Promise<void>;
  /** Apply HIDE (APPROVED→HIDDEN) or SHOW (HIDDEN→APPROVED), then refresh. Toasts. */
  setVisibility: (attachmentId: string, action: MediaVisibilityAction) => Promise<void>;
}

function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

export function useQuestionMedia({
  questionId,
  token,
  enabled,
}: UseQuestionMediaOptions): UseQuestionMediaResult {
  const [media, setMedia] = useState<AdminMediaListItem[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMutating, setIsMutating] = useState(false);

  const { showSuccess, showError } = useToast();
  const { uploadQuestionImage } = useMediaAuthoring(token);

  const abortRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);
  // Synchronous mutation guard (useReplies/useRfi precedent): React state is
  // async, so two same-tick clicks could both read a stale isMutating=false;
  // this ref flips synchronously so exactly one API call is issued.
  const isMutatingRef = useRef(false);
  // List-result generation counter. Uploads are NOT serialized with the
  // management mutations (they can take seconds), so an optimistic prepend
  // (fold E1) can race a concurrent management-action refetch whose SELECT ran
  // before the upload committed: without this guard that stale refetch's blind
  // setMedia would land AFTER the prepend and silently drop the just-uploaded
  // row. Every intent that publishes list state (a refresh, or an optimistic
  // prepend) bumps the generation; a refresh only applies its fetched result
  // if its generation is still current. Decoupled from `abortRef`, which stays
  // purely for fetch cancellation. (PR4b internal adversarial review.)
  const listGenerationRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  // Reset to the not-yet-loaded state whenever the target question changes so
  // a previously fetched list can never render against a different question.
  useEffect(() => {
    setMedia(null);
  }, [questionId, token]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!token || !questionId) return;

    // Claim the latest list-result generation (see listGenerationRef) so a
    // later optimistic prepend — or a newer refresh — supersedes this one's
    // result even though its fetch is still in flight.
    const generation = ++listGenerationRef.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    try {
      // anchor: 'question' (fold C1) — the question's OWN gallery must exclude
      // reply-anchored rows, which carry the denormalized question_id and
      // would otherwise misattribute into this list.
      const res = await adminApi.media.list(
        token,
        { questionId, anchor: 'question', limit: QUESTION_MEDIA_LIST_LIMIT },
        controller.signal
      );
      if (!isMountedRef.current || listGenerationRef.current !== generation) return;
      if (res.success) {
        setMedia(res.data.media);
      }
      // On a failed list fetch the modal keeps its current render (stale
      // snapshot or previous fetch) — management actions still re-fetch.
    } catch (err) {
      if (isAbortError(err)) return; // superseded — silently ignore
    } finally {
      if (isMountedRef.current && listGenerationRef.current === generation) {
        setIsLoading(false);
      }
    }
  }, [token, questionId]);

  useEffect(() => {
    if (enabled) {
      void refresh();
    }
  }, [enabled, refresh]);

  const uploadImage = useCallback(
    async (file: File): Promise<MediaUploadOutcome> => {
      const outcome = await uploadQuestionImage(questionId, file);
      if (outcome.success && isMountedRef.current) {
        const created = outcome.media;
        if (created && media !== null) {
          // Fold E1: land the returned DTO directly instead of a per-file
          // list refetch (a multi-file batch fired one fetch per file). The
          // list is server-ordered newest-first, so PREPEND preserves it.
          //
          // Supersede any in-flight refresh (bump the generation + abort its
          // now-pointless fetch) so a concurrent management-action refetch
          // whose SELECT predates this commit cannot land afterward and drop
          // the just-uploaded row. This optimistic result IS the newest truth,
          // so clear the list-loading flag the superseded refresh set.
          listGenerationRef.current++;
          abortRef.current?.abort();
          abortRef.current = null;
          setIsLoading(false);
          setMedia((current) => (current === null ? current : [created, ...current]));
        } else {
          // First upload before any successful list load (the modal is still
          // on the stale snapshot) — a refetch establishes fresh server truth.
          await refresh();
        }
      }
      return outcome;
    },
    [uploadQuestionImage, questionId, media, refresh]
  );

  /** Shared claim/release around the two management mutations. */
  const runMutation = useCallback(
    async (mutate: () => Promise<void>): Promise<void> => {
      if (isMutatingRef.current) return;
      isMutatingRef.current = true;
      setIsMutating(true);
      try {
        await mutate();
      } finally {
        isMutatingRef.current = false;
        if (isMountedRef.current) setIsMutating(false);
      }
    },
    []
  );

  const removeImage = useCallback(
    async (attachmentId: string): Promise<void> => {
      await runMutation(async () => {
        try {
          const res = await adminApi.media.remove(token, attachmentId);
          if (!isMountedRef.current) return;
          if (res.success) {
            showSuccess(res.data.message);
            await refresh();
          } else {
            // D-LEGALHOLD 409 → the explicit hold copy; otherwise the server
            // message (admin surface — no anti-tip-off constraint).
            showError(
              resolveMediaErrorMessage(res.error, {
                fallback: 'Failed to remove the image.',
              })
            );
          }
        } catch {
          if (!isMountedRef.current) return;
          showError('Failed to remove the image.');
        }
      });
    },
    [runMutation, token, showSuccess, showError, refresh]
  );

  const setVisibility = useCallback(
    async (attachmentId: string, action: MediaVisibilityAction): Promise<void> => {
      await runMutation(async () => {
        try {
          const res = await adminApi.media.moderate(token, attachmentId, action);
          if (!isMountedRef.current) return;
          if (res.success) {
            showSuccess(res.data.message);
            await refresh();
          } else {
            // SHOW on a held row 409s with details.legalHold (D-LEGALHOLD).
            showError(
              resolveMediaErrorMessage(res.error, {
                fallback: 'Failed to update the image visibility.',
              })
            );
          }
        } catch {
          if (!isMountedRef.current) return;
          showError('Failed to update the image visibility.');
        }
      });
    },
    [runMutation, token, showSuccess, showError, refresh]
  );

  return { media, isLoading, isMutating, refresh, uploadImage, removeImage, setVisibility };
}
